import React, { useState } from 'react';
import { auth } from './firebase';
import './SignUp.css';
import logo from './ayla-logo.png';
import PolicyModal from './PolicyModal';

const SignUp = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [consentError, setConsentError] = useState('');

  const [openPolicy, setOpenPolicy] = useState(null); // 'privacy' | 'terms' | null

  const handleSignUp = (e) => {
    e.preventDefault();

    if (!acceptedPrivacy || !acceptedTerms) {
      setConsentError('Please accept the Privacy Policy and Terms & Conditions to create an account.');
      return;
    }

    setConsentError('');

    auth.createUserWithEmailAndPassword(email, password)
      .then((userCredential) => {
        // Signed in
        const user = userCredential.user;
        return user.updateProfile({
          displayName: name
        });
      })
      .catch((error) => {
        alert(error.message);
      });
  };

  return (
    <div className="sign-up-container">
      <div className="sign-up-form">
        <img src={logo} alt="AYLA" className="logo" />
        <h1>Create a vendor account</h1>
        <form onSubmit={handleSignUp}>
          <input
            type="text"
            placeholder="First & last name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Choose password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <div className="consents">
            <label className="consent-row">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => {
                  setAcceptedPrivacy(e.target.checked);
                  setConsentError('');
                }}
              />
              <span>
                I agree to the{' '}
                <button type="button" className="policy-link" onClick={() => setOpenPolicy('privacy')}>
                  Privacy Policy
                </button>
              </span>
            </label>

            <label className="consent-row">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  setConsentError('');
                }}
              />
              <span>
                I agree to the{' '}
                <button type="button" className="policy-link" onClick={() => setOpenPolicy('terms')}>
                  Terms & Conditions
                </button>
              </span>
            </label>

            {consentError ? <div className="consent-error">{consentError}</div> : null}
          </div>

          <div className="form-buttons">
            <button type="button" className="cancel-btn">CANCEL</button>
            <button
              type="submit"
              className="save-btn"
              disabled={!acceptedPrivacy || !acceptedTerms}
              title={!acceptedPrivacy || !acceptedTerms ? 'Please accept the Privacy Policy and Terms & Conditions' : undefined}
            >
              SAVE
            </button>
          </div>
        </form>
        <p>Already have an account? <a href="/signin">Sign in</a></p>
      </div>

      <PolicyModal
        title="Privacy Policy"
        open={openPolicy === 'privacy'}
        onClose={() => setOpenPolicy(null)}
      >
        <h5>AYLA – PRIVACY POLICY</h5>
        <p><strong>Effective Date:</strong> 22 January 2026</p>
        <p><strong>Jurisdiction:</strong> Italy / European Union</p>
        <p><strong>Status:</strong> Early Access / Beta</p>

        <p>
          AYLA (“AYLA”, “we”, “us”, “our”) is committed to protecting your personal data and processing it in accordance with Regulation (EU)
          2016/679 (“GDPR”) and applicable Italian data protection laws.
        </p>
        <p>
          This Privacy Policy explains how personal data is collected, used, and protected when you access or use the AYLA platform
          (“Platform”).
        </p>

        <h5>1. Beta / Early Access Notice</h5>
        <p>
          The Platform is currently provided in an early access (beta) version. Certain features, functionalities, and data processing
          activities may evolve, change, or be refined as the Platform develops.
        </p>

        <h5>2. Data Controller</h5>
        <p>The data controller responsible for your personal data pursuant to Regulation (EU) 2016/679 (GDPR) is:</p>
        <p>
          <strong>AYLA – company in formation</strong>
          <br />
          Registered office: Italy
          <br />
          Email: <a href="mailto:privacy@aylaarchives.com">privacy@aylaarchives.com</a>
        </p>

        <h5>3. Categories of Personal Data Collected</h5>
        <p>Depending on how you use the Platform, we may collect:</p>

        <h6>a) Account &amp; Profile Data</h6>
        <ul>
          <li>Name</li>
          <li>Email address</li>
          <li>Profile information (designer or stylist)</li>
          <li>Content uploaded to the Platform (images, garments, descriptions)</li>
        </ul>

        <h6>b) Usage &amp; Technical Data</h6>
        <ul>
          <li>IP address</li>
          <li>Device and browser type</li>
          <li>Log data</li>
          <li>Platform interactions</li>
        </ul>

        <h6>c) Communications</h6>
        <ul>
          <li>Messages sent via the Platform</li>
          <li>Support inquiries</li>
        </ul>

        <h5>4. Purposes of Processing</h5>
        <p>Your personal data is processed for the following purposes:</p>
        <ul>
          <li>Creating and managing user accounts</li>
          <li>Enabling designers to showcase content</li>
          <li>Enabling stylists to discover and connect with designers</li>
          <li>Operating, maintaining, and improving the Platform</li>
          <li>AI-assisted features (recommendations, categorization, visualization)</li>
          <li>Communications and support</li>
          <li>Legal and security purposes</li>
        </ul>

        <h5>5. Legal Basis for Processing</h5>
        <p>Processing is based on one or more of the following legal grounds:</p>
        <ul>
          <li>Consent (e.g. account creation, cookies, AI features)</li>
          <li>Contractual necessity (providing the Platform)</li>
          <li>Legitimate interest (platform improvement, security, analytics)</li>
        </ul>

        <h5>6. Artificial Intelligence Processing</h5>
        <p>AYLA uses artificial intelligence technologies to support Platform functionality, including:</p>
        <ul>
          <li>Content enhancement and visualization</li>
          <li>Categorization and recommendations</li>
          <li>Platform optimization</li>
        </ul>
        <p>AI systems do not produce automated decisions with legal or similarly significant effects on users.</p>
        <p>User content and usage data may be processed to improve AI performance solely for internal purposes.</p>

        <h5>7. Data Retention</h5>
        <p>
          Personal data is retained only for as long as necessary to fulfill the purposes described in this Policy or to comply with legal
          obligations. Users may request deletion of their account at any time.
        </p>

        <h5>8. Data Sharing</h5>
        <p>AYLA does not sell personal data.</p>
        <p>Data may be shared with:</p>
        <ul>
          <li>Technical service providers (hosting, analytics)</li>
          <li>AI service providers (for platform functionality)</li>
          <li>Authorities, if required by law</li>
        </ul>
        <p>All service providers are subject to appropriate data protection safeguards.</p>

        <h5>9. Data Transfers</h5>
        <p>
          Personal data may be processed within the European Union or in countries that provide an adequate level of data protection in
          accordance with GDPR.
        </p>

        <h5>10. User Rights</h5>
        <p>Under GDPR, users have the right to:</p>
        <ul>
          <li>Access personal data</li>
          <li>Rectify inaccurate data</li>
          <li>Request deletion (“right to be forgotten”)</li>
          <li>Restrict or object to processing</li>
          <li>Data portability</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p>
          Requests can be submitted via email{' '}
          <a href="mailto:privacy@aylaarchives.com">privacy@aylaarchives.com</a>.
        </p>

        <h5>11. Security</h5>
        <p>
          AYLA implements appropriate technical and organizational measures to protect personal data against unauthorized access, loss, or
          misuse.
        </p>

        <h5>12. Updates to This Policy</h5>
        <p>AYLA may update this Privacy Policy from time to time. Changes will be published on the Platform.</p>

        <h5>13. Contact</h5>
        <p>
          For privacy-related questions or requests, please contact:{' '}
          <a href="mailto:privacy@aylaarchives.com">privacy@aylaarchives.com</a>
        </p>
      </PolicyModal>

      <PolicyModal
        title="Terms & Conditions"
        open={openPolicy === 'terms'}
        onClose={() => setOpenPolicy(null)}
      >
        <h5>AYLA – TERMS &amp; CONDITIONS</h5>
        <p><strong>Effective Date:</strong> 22 January 2026</p>
        <p><strong>Jurisdiction:</strong> Italy</p>
        <p><strong>Status:</strong> Early Access / Beta</p>

        <h5>1. Introduction</h5>
        <p>
          These Terms and Conditions (“Terms”) govern access to and use of the AYLA platform (the “Platform”), operated by AYLA (company in
          formation) (“AYLA”, “we”, “us”, “our”).
        </p>
        <p>
          By accessing or using the Platform, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not
          agree, you must not use the Platform.
        </p>

        <h5>2. Platform Description</h5>
        <p>
          AYLA operates as a digital showcase and connection platform for fashion designers and industry professionals. AYLA is a digital
          platform created to:
        </p>
        <ul>
          <li>Showcase the work of emerging fashion designers; and</li>
          <li>Enable stylists to discover and connect directly with designers for creative projects.</li>
        </ul>
        <p>AYLA is not:</p>
        <ul>
          <li>A marketplace or e-commerce platform</li>
          <li>A rental, resale, logistics, insurance, or payment service</li>
          <li>A party to any agreement between users</li>
        </ul>
        <p>All collaborations occur directly between users, outside the Platform.</p>

        <h5>3. User Types</h5>
        <p>The Platform is available exclusively to:</p>
        <ul>
          <li>Designers, and</li>
          <li>Stylists</li>
        </ul>
        <p>No other user categories are currently supported.</p>

        <h5>4. Eligibility</h5>
        <p>To use the Platform, you must:</p>
        <ul>
          <li>Be at least 18 years old; and</li>
          <li>Have the legal capacity to enter into binding agreements.</li>
        </ul>
        <p>By using AYLA, you represent and warrant that you meet these requirements.</p>

        <h5>5. Account Registration &amp; Security</h5>
        <p>To access certain features, users must create an account.</p>
        <p>You agree to:</p>
        <ul>
          <li>Provide accurate and complete information;</li>
          <li>Keep login credentials confidential; and</li>
          <li>Be fully responsible for all activity occurring under your account.</li>
        </ul>
        <p>AYLA is not responsible for unauthorized access resulting from your failure to secure your account.</p>

        <h5>6. Early Access / Beta Disclaimer</h5>
        <p>The Platform is provided in early access (beta) form. This means that:</p>
        <ul>
          <li>Features may be incomplete, experimental, or change over time;</li>
          <li>Bugs, errors, or interruptions may occur;</li>
          <li>Certain functionalities may be modified, suspended, or removed without notice.</li>
        </ul>
        <p>The Platform is provided “as is” and “as available.”</p>

        <h5>7. User Content &amp; Intellectual Property</h5>

        <h6>7.1 Ownership</h6>
        <p>
          All content uploaded, posted, or otherwise made available by users, including but not limited to images, designs, garments,
          photographs, videos, text, and descriptions (“User Content”), remains the exclusive property of the user who submits it.
        </p>
        <p>AYLA does not claim ownership over User Content.</p>

        <h6>7.2 User Representations &amp; Warranties</h6>
        <p>By uploading or submitting User Content, users represent and warrant that:</p>
        <ul>
          <li>They are the sole owner of the User Content, or</li>
          <li>They hold all necessary rights, licenses, permissions, and authorizations to upload and share the User Content;</li>
        </ul>
        <p>and that:</p>
        <ul>
          <li>The User Content does not infringe any third-party intellectual property, privacy, or other rights;</li>
          <li>The User Content complies with applicable laws.</li>
        </ul>
        <p>Users remain solely responsible for the User Content they upload.</p>

        <h6>7.3 License Granted to AYLA</h6>
        <p>
          For the sole purpose of operating, maintaining, and promoting the Platform, users grant AYLA a non-exclusive, worldwide,
          royalty-free, revocable license to host, display, reproduce, and make available User Content on the Platform and in connection with
          AYLA-related promotional activities.
        </p>
        <p>
          This license does not transfer ownership and may be revoked by removing the content, except where retention is required by law.
        </p>

        <h5>8. Artificial Intelligence Use</h5>
        <p>AYLA may use artificial intelligence technologies to support Platform functionality, including:</p>
        <ul>
          <li>Content categorization;</li>
          <li>Recommendations;</li>
          <li>Visual enhancement or discovery tools.</li>
        </ul>
        <p>
          AI outputs are informational and supportive only and do not replace professional or creative judgment. AYLA does not use AI to make
          automated decisions producing legal or similarly significant effects on users.
        </p>

        <h5>9. No Responsibility for Garments, Loss, or Damage</h5>
        <p>
          AYLA does not take possession of, store, ship, insure, or otherwise handle any garments, samples, or physical items.
        </p>
        <p>AYLA shall not be responsible or liable for:</p>
        <ul>
          <li>Loss, theft, damage, delay, or misuse of garments;</li>
          <li>Wear and tear, defects, or alterations;</li>
          <li>Any issues arising from transport, handling, or use of garments.</li>
        </ul>
        <p>All risks related to garments or physical items remain solely between the users involved.</p>

        <h5>10. No Transactions, Intermediation, or Guarantees</h5>
        <p>AYLA does not act as an intermediary, agent, broker, or representative of any user. AYLA:</p>
        <ul>
          <li>Does not facilitate payments;</li>
          <li>Does not set collaboration terms;</li>
          <li>Does not guarantee collaborations, outcomes, or performance.</li>
        </ul>
        <p>All collaborations and agreements occur directly between users, at their own risk.</p>

        <h5>11. Fees</h5>
        <p>
          The Platform is currently provided free of charge. AYLA reserves the right to introduce paid features or monetization models in the
          future. Any changes will be communicated in advance.
        </p>

        <h5>12. Social Media &amp; Promotional Use</h5>
        <p>For the purpose of promoting the Platform and its community, AYLA may feature User Content on:</p>
        <ul>
          <li>The AYLA website;</li>
          <li>AYLA social media channels;</li>
          <li>Newsletters or promotional materials related to AYLA.</li>
        </ul>
        <p>
          Where reasonably possible, AYLA will credit the relevant Designer or user. Designers may request exclusion from future promotional
          campaigns by contacting <a href="mailto:privacy@aylaarchives.com">privacy@aylaarchives.com</a>. AYLA will use reasonable efforts to
          accommodate such requests.
        </p>
        <p>Featuring User Content does not constitute endorsement or guarantee by AYLA.</p>

        <h5>13. Copyright &amp; Takedown Requests</h5>
        <p>
          If you believe that content infringes your intellectual property rights, you may contact{' '}
          <a href="mailto:privacy@aylaarchives.com">privacy@aylaarchives.com</a>. AYLA reserves the right to remove content or restrict access
          pending review.
        </p>

        <h5>14. Prohibited Conduct</h5>
        <p>Users must not:</p>
        <ul>
          <li>Violate laws or third-party rights;</li>
          <li>Upload unlawful, misleading, or harmful content;</li>
          <li>Misuse the Platform or attempt unauthorized access;</li>
          <li>Use the Platform for commercial exploitation outside its intended purpose.</li>
        </ul>

        <h5>15. Termination</h5>
        <p>
          Users may delete their account at any time. AYLA reserves the right to suspend or terminate access, with or without notice, in case
          of:
        </p>
        <ul>
          <li>Violation of these Terms;</li>
          <li>Unlawful conduct;</li>
          <li>Risk to the Platform or other users.</li>
        </ul>

        <h5>16. Exclusion of Warranties</h5>
        <p>To the maximum extent permitted by law, AYLA disclaims all warranties, express or implied, including:</p>
        <ul>
          <li>Fitness for a particular purpose;</li>
          <li>Availability or uninterrupted access;</li>
          <li>Accuracy or reliability of content.</li>
        </ul>

        <h5>17. Limitation of Liability</h5>
        <p>AYLA shall not be liable for:</p>
        <ul>
          <li>Indirect, incidental, or consequential damages;</li>
          <li>Loss of data, profits, or opportunities;</li>
          <li>User interactions or collaborations.</li>
        </ul>
        <p>Nothing in these Terms limits liability where such limitation is prohibited by law.</p>

        <h5>18. Indemnification</h5>
        <p>You agree to indemnify and hold harmless AYLA from any claims, damages, or expenses arising from:</p>
        <ul>
          <li>Your use of the Platform;</li>
          <li>Your content;</li>
          <li>Your violation of these Terms or third-party rights.</li>
        </ul>

        <h5>19. Privacy &amp; Cookies</h5>
        <p>
          Personal data is processed in accordance with AYLA’s Privacy Policy and Cookie Policy, available on the Platform.
        </p>

        <h5>20. Changes to the Terms</h5>
        <p>
          AYLA may update these Terms from time to time. Continued use of the Platform constitutes acceptance of the updated Terms.
        </p>

        <h5>21. Governing Law &amp; Jurisdiction</h5>
        <p>
          These Terms are governed by Italian law. Any disputes shall be subject to the exclusive jurisdiction of the courts of Milan, Italy.
        </p>

        <h5>22. Contact</h5>
        <p>
          For legal or contractual inquiries, please contact{' '}
          <a href="mailto:privacy@aylaarchives.com">privacy@aylaarchives.com</a>.
        </p>
      </PolicyModal>
    </div>
  );
};

export default SignUp;
